import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { deriveResult, gameStatusLabel, sideOutcomeLabel, teamLabel } from '@region13/shared-types'
import { api, type CoachTeamWithGames, type SeasonGame } from '../lib/api'
import { Card } from '../components/ui/Card'
import { CardSection } from '../components/ui/CardSection'
import { ListSkeleton } from '../components/ui/ListSkeleton'
import { TeamScope } from '../components/TeamScope'
import { AwayGamesCard } from '../components/AwayGamesCard'
import { gameLine, hasStarted, isUpcoming, opponentOf } from '../lib/nextGame'
import { goTo } from '../lib/goTo'
import { SCHEDULE_NOT_PUBLISHED_MESSAGE } from '../lib/scheduleCopy'

export const Route = createFileRoute('/games')({ component: Games })

/** Shown on a played game whose week the stats admins have not published.
 *  The score may already be reviewed; it is held back on purpose. */
export const RESULT_PENDING = 'Result pending'
/** Shown on a published game in a card-collecting division with no score row. */
export const NO_SCORE_RECORDED = 'No score recorded'

/** "Win · B10-01 3 - 1 B10-02", home team first as `deriveResult`'s notation
 *  is, with the outcome word from this team's side ("Draw", never "Tie"). */
export function resultLine(game: SeasonGame, teamExt: string): string | null {
  if (!game.score) return null
  const result = deriveResult(game.score)
  const opponent = opponentOf(game, teamExt)
  const [home, visitor] = game.side === 'home' ? [teamExt, opponent] : [opponent, teamExt]
  return `${sideOutcomeLabel(result.outcome, game.side)} · ${home} ${result.notation} ${visitor}`
}

function lineFor(game: SeasonGame, teamExt: string): string {
  const line = gameLine(game, opponentOf(game, teamExt))
  const marked = game.isFriendly ? `${line} · Friendly` : line
  // Same trailing marker as Friendly: a called-off game stays on the page.
  const status = gameStatusLabel(game.gameStatus)
  return status ? `${marked} · ${status}` : marked
}

/** What a played game shows under its line. A game stats collects no card
 *  for (an Area/Section fixture, a division below the card age) never has a
 *  score, so it shows nothing rather than a gap. */
function PlayedOutcome({ game, teamExt }: { game: SeasonGame; teamExt: string }) {
  // A cancelled or forfeited game has no card, so no score line or "pending".
  if (!game.tracked || gameStatusLabel(game.gameStatus)) return null
  if (!game.published) return <span className="text-ink-soft">{RESULT_PENDING}</span>
  return (
    <span className="flex flex-wrap gap-x-3 text-ink">
      <span className="font-semibold">{resultLine(game, teamExt) ?? NO_SCORE_RECORDED}</span>
      {game.sportsmanship !== null && <span>Sportsmanship {game.sportsmanship}/5</span>}
    </span>
  )
}

/** Opens the incident wizard for one game. The signed link is fetched on
 *  click, one request per click, not one per row. */
function ReportInjury({ teamExt, gameId }: { teamExt: string; gameId: string }) {
  const [state, setState] = useState<'idle' | 'busy' | 'error'>('idle')
  async function go() {
    setState('busy')
    try {
      const links = await api.reportLinks(teamExt, gameId)
      goTo(links.incident)
    } catch {
      setState('error')
    }
  }
  return (
    <span className="flex items-center gap-2">
      <button type="button" onClick={go} disabled={state === 'busy'}
        className="underline text-brand-maroon hover:text-brand-red-dark disabled:opacity-50">
        Report an injury
      </button>
      {state === 'error' && <span role="alert" className="text-brand-red-dark">Couldn't open the report. Please try again.</span>}
    </span>
  )
}

function GamesBody({ games, teamExt }: { games: SeasonGame[]; teamExt: string }) {
  if (games.length === 0) return <p className="text-sm text-ink-soft">{SCHEDULE_NOT_PUBLISHED_MESSAGE}</p>
  // Games arrive in date order, so one split point separates played from upcoming.
  const split = games.findIndex((g) => isUpcoming(g.date))
  const end = split === -1 ? games.length : split
  // slice() before reverse(): `games` is react-query's cached array, and
  // reversing it in place flips the order again on every render.
  const played = games.slice(0, end).reverse()
  const upcoming = games.slice(end)
  return (
    <>
      {upcoming.length > 0 && (
        <CardSection title="Upcoming">
          <ul className="flex flex-col gap-1 text-sm text-ink">
            {upcoming.map((g) => (
              <li key={g.id} className="flex flex-col gap-0.5">
                <span>{lineFor(g, teamExt)}</span>
                {hasStarted(g.date) && <ReportInjury teamExt={teamExt} gameId={g.id} />}
              </li>
            ))}
          </ul>
        </CardSection>
      )}
      {played.length > 0 && (
        <CardSection title="Played">
          <ul className="flex flex-col gap-2 text-sm">
            {played.map((g) => (
              <li key={g.id} className="flex flex-col gap-0.5">
                <span className="text-ink">{lineFor(g, teamExt)}</span>
                <PlayedOutcome game={g} teamExt={teamExt} />
                <ReportInjury teamExt={teamExt} gameId={g.id} />
              </li>
            ))}
          </ul>
        </CardSection>
      )}
    </>
  )
}

/** One team's season: upcoming games, then played games newest first with
 *  the score and this team's sportsmanship once the week is published.
 *  Exported so tests can render it directly, like `TeamStandings`. */
export function TeamGames({ team }: { team: CoachTeamWithGames }) {
  const q = useQuery({
    queryKey: ['season-games', team.externalId],
    queryFn: () => api.seasonGames(team.externalId),
  })
  return (
    <Card as="section">
      <div>
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{team.division}</p>
      </div>
      {q.isLoading ? (
        <ListSkeleton label="Loading games" />
      ) : q.isError ? (
        <p role="alert" className="text-sm text-brand-red-dark">Couldn't load games. Please try again.</p>
      ) : (
        <GamesBody games={q.data!.games} teamExt={team.externalId} />
      )}
      <AwayGamesCard teamExternalId={team.externalId} />
    </Card>
  )
}

function Games() {
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to see its games."
    >
      {(team) => <TeamGames team={team} />}
    </TeamScope>
  )
}
